/**
 * GM Permission Level System (WoW-Style)
 *
 * Defines 6 permission tiers and a command registry that maps every slash command
 * to its minimum required GM level. Shared between client and server.
 *
 * To add a new command: add one entry to COMMAND_PERMISSIONS.
 * To change a command's tier: change the value in COMMAND_PERMISSIONS.
 */

/** GM permission levels — higher number = more power */
export const GM_LEVEL = {
  PLAYER: 0,       // Social/RP commands only
  PLAYER_PLUS: 1,  // Read-only info commands
  SUPPORT: 2,      // Moderation tools
  GM_LOW: 3,       // Self-targeting cheats
  GM_HIGH: 4,      // World/map manipulation
  ADMIN: 5,        // System-level control
} as const;

export type GMLevel = (typeof GM_LEVEL)[keyof typeof GM_LEVEL];

export const GM_LEVEL_NAMES: Record<GMLevel, string> = {
  [GM_LEVEL.PLAYER]: 'Player',
  [GM_LEVEL.PLAYER_PLUS]: 'Player+',
  [GM_LEVEL.SUPPORT]: 'Support',
  [GM_LEVEL.GM_LOW]: 'GM',
  [GM_LEVEL.GM_HIGH]: 'Senior GM',
  [GM_LEVEL.ADMIN]: 'Admin',
};

/**
 * Command permission registry.
 * Maps command name -> minimum GM level required to execute.
 * Unlisted commands default to GM_LOW (3).
 */
export const COMMAND_PERMISSIONS: Record<string, GMLevel> = {
  // ── Level 0: Player — Social/RP emotes ──
  help: GM_LEVEL.PLAYER,
  jobs: GM_LEVEL.PLAYER, // Opens the player's Jobs window; advancement remains server-authoritative.
  duel: GM_LEVEL.PLAYER, // Consensual 1v1 — ALL players can issue/accept/decline/forfeit
  sit: GM_LEVEL.PLAYER,
  drink: GM_LEVEL.PLAYER,
  eat: GM_LEVEL.PLAYER,
  love: GM_LEVEL.PLAYER,
  wave: GM_LEVEL.PLAYER,
  dance: GM_LEVEL.PLAYER,
  bow: GM_LEVEL.PLAYER,

  // ── Level 1: Player+ — Read-only info ──
  stats: GM_LEVEL.PLAYER_PLUS,
  maps: GM_LEVEL.PLAYER_PLUS,
  mapinfo: GM_LEVEL.PLAYER_PLUS,
  platforms: GM_LEVEL.PLAYER_PLUS,
  renderstate: GM_LEVEL.PLAYER_PLUS,
  renderdump: GM_LEVEL.PLAYER_PLUS,
  datastate: GM_LEVEL.PLAYER_PLUS,
  debugmap: GM_LEVEL.PLAYER_PLUS,
  'kernel-systems': GM_LEVEL.PLAYER_PLUS,

  // ── Level 2: Support — Moderation ──
  announce: GM_LEVEL.SUPPORT,
  // kick: GM_LEVEL.SUPPORT,   // future
  // mute: GM_LEVEL.SUPPORT,   // future
  // warn: GM_LEVEL.SUPPORT,   // future

  // ── Level 3: GM Low — Self-targeting cheats ──
  tp: GM_LEVEL.GM_LOW,
  heal: GM_LEVEL.GM_LOW,
  mana: GM_LEVEL.GM_LOW,
  damage: GM_LEVEL.GM_LOW,
  kill: GM_LEVEL.GM_LOW,
  spawn: GM_LEVEL.GM_LOW,
  xp: GM_LEVEL.GM_LOW,
  gold: GM_LEVEL.GM_LOW,
  level: GM_LEVEL.GM_LOW,
  levelup: GM_LEVEL.GM_LOW,
  god: GM_LEVEL.GM_LOW,
  invincible: GM_LEVEL.GM_LOW,
  speed: GM_LEVEL.GM_LOW,
  ability: GM_LEVEL.GM_LOW,
  tracker: GM_LEVEL.GM_LOW,
  qa_scenario: GM_LEVEL.GM_LOW,

  // ── Level 4: GM High — World/map manipulation ──
  killall: GM_LEVEL.GM_HIGH,
  pvp: GM_LEVEL.GM_HIGH, // Live tunes per-room PvP config (friendly fire, respawn, bots, …)
  clear: GM_LEVEL.GM_HIGH,
  noclip: GM_LEVEL.GM_HIGH,
  class: GM_LEVEL.GM_HIGH,
  load: GM_LEVEL.GM_HIGH,
  map: GM_LEVEL.GM_HIGH,
  reloadmap: GM_LEVEL.GM_HIGH,
  spawn_test: GM_LEVEL.GM_HIGH,
  spawn_all_test: GM_LEVEL.GM_HIGH,
  spawn_mob: GM_LEVEL.GM_HIGH,
  hold_mobs: GM_LEVEL.GM_HIGH, // debug_holdMobs: calm mobs stand still, for review shots
  hurt_mob: GM_LEVEL.GM_HIGH, // debug_hurtMob: a mob down to a share of its health, for boss phase checks
  gcd: GM_LEVEL.GM_HIGH,
  item: GM_LEVEL.GM_HIGH,
  bot: GM_LEVEL.GM_HIGH,
  // /sim runs a controlled bot-vs-bot tournament + writes a JSONL record;
  // worst case is "annoying other players in the room". DJ wants this
  // accessible from URL auto-pick characters on PTR (gmLevel=0 by default).
  // Bumped from GM_HIGH → PLAYER 2026-04-29 — restore to GM_HIGH if/when
  // PTR becomes a public-link surface or before prod cutover.
  sim: GM_LEVEL.PLAYER,

  // ── GM Event commands ──
  // Management commands are GM_HIGH (create/start/cancel/warp/tag/next/end).
  // Participation commands are PLAYER (join/leave/list) — any player can
  // opt into an event.
  event:           GM_LEVEL.GM_HIGH,
  eventcreate:     GM_LEVEL.GM_HIGH,
  eventstart:      GM_LEVEL.GM_HIGH,
  eventcancel:     GM_LEVEL.GM_HIGH,
  eventwarp:       GM_LEVEL.GM_HIGH,
  eventtag:        GM_LEVEL.GM_HIGH,
  eventnext:       GM_LEVEL.GM_HIGH,
  eventend:        GM_LEVEL.GM_HIGH,
  eventtransfer:   GM_LEVEL.GM_HIGH,
  eventjoin:       GM_LEVEL.PLAYER,
  eventleave:      GM_LEVEL.PLAYER,
  eventlist:       GM_LEVEL.PLAYER,

  // ── Level 5: Admin — System-level ──
  setgm: GM_LEVEL.ADMIN,
  // featureflag: GM_LEVEL.ADMIN,   // future
  // maintenance: GM_LEVEL.ADMIN,   // future
};

/** Default permission for unlisted commands */
const DEFAULT_REQUIRED_LEVEL = GM_LEVEL.GM_LOW;

/**
 * TEMPORARY: open all chat slash commands to every player.
 *
 * Set true 2026-04-25 at DJ's request for an ad-hoc dev/play session.
 * When true, `canExecuteCommand` returns true regardless of `gmLevel`,
 * so any connected client can use `/spawn`, `/heal`, `/tp`, `/setgm`, etc.
 *
 * This is a deliberate trust-the-room posture for the live PTR session;
 * the studio-ops OPEN_ITEMS entry filed for revert is the audit trail.
 *
 * REVERT BEFORE: prod cutover, public-link sharing, or any session DJ
 * doesn't actively trust the participant list of.
 */
const TEMP_OPEN_CONSOLE_FOR_ALL = false;

/**
 * Check if a GM level can execute a given command.
 *
 * @deprecated Phase 5 (gap-closing) — prefer
 *   `requireGmForCommand(identity, command)` from
 *   `src/server/auth/identity.ts`. The new helper takes the unified
 *   `RequestIdentity` (post Arc C.3 enrichment) instead of a raw
 *   gmLevel number, returns a structured `{ok, reason}` instead of a
 *   bare boolean, and stays consistent with the rest of the auth
 *   model. This function is preserved as the underlying lookup;
 *   `requireGmForCommand` calls it. Both paths read the same
 *   COMMAND_PERMISSIONS table + TEMP_OPEN_CONSOLE_FOR_ALL flag.
 */
export function canExecuteCommand(gmLevel: number, command: string): boolean {
  if (TEMP_OPEN_CONSOLE_FOR_ALL) return true;
  const required = COMMAND_PERMISSIONS[command] ?? DEFAULT_REQUIRED_LEVEL;
  return gmLevel >= required;
}

/** Get all commands available at a given GM level. */
export function getAvailableCommands(gmLevel: number): string[] {
  return Object.entries(COMMAND_PERMISSIONS)
    .filter(([_, required]) => gmLevel >= required)
    .map(([cmd]) => cmd);
}

/** Get the required level for a command. */
export function getRequiredLevel(command: string): GMLevel {
  return COMMAND_PERMISSIONS[command] ?? DEFAULT_REQUIRED_LEVEL;
}


/**
 * Memory-mode harness elevation (2026-07-02 tooling arc). In memory-only dev
 * mode (no DB — the local probe/e2e stack), dev-token sessions get GM_LOW so
 * self-targeting debug affordances (debug_heal etc.) actually work: the e2e
 * heal assist was a silent "Insufficient permissions" no-op for the whole
 * green-run campaign. STRICTLY memory-mode — Mongo tiers (PTR/prod) never
 * take this path, so public dev-token joins there keep gmLevel from auth.
 */
export function resolveJoinGmLevel(authGmLevel: number, isMemoryModeDev: boolean): number {
  return isMemoryModeDev ? Math.max(authGmLevel, GM_LEVEL.GM_LOW) : authGmLevel;
}
